// The single place that decides "this session is over".
//
// A dead session doesn't fail once, it fails N times at once: every request the
// page had in flight comes back 401 together. The old code let each of those
// call `window.location.href` on its own, which is why the redirect felt random
// — whichever request lost the race won the navigation. This module latches the
// first failure and drops the rest, so a burst produces exactly one redirect.
//
// It deliberately knows nothing about Redux. `store/slices/authSlice` imports
// `lib/axios`, so anything axios imports must not reach back into the store.

type Listener = () => void;

const listeners = new Set<Listener>();
let expired = false;

export function onSessionExpired(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Called by the axios interceptor once a 401 is terminal — the refresh itself
 * failed, so there is nothing left to retry with. Idempotent: only the first
 * call in a burst notifies.
 */
export function notifySessionExpired() {
  if (expired) return;
  expired = true;
  listeners.forEach((listener) => listener());
}

/**
 * Re-arms the latch. Called after a fresh login, otherwise the next expiry in
 * the same tab would be swallowed by the flag set during the previous one.
 */
export function resetSessionExpiry() {
  expired = false;
}

export function isSessionExpired() {
  return expired;
}
